// SPDX-FileCopyrightText: 2026 Stadtplatz.de
//
// SPDX-License-Identifier: EUPL-1.2

import React, { useState, useEffect } from "react";
import { useKeycloak } from "../../context/KeycloakProvider";
import { bookingService } from "../../services/bookingService";
import Bookings from "./bookings"; // Import from the same directory
import { Loader2, AlertCircle } from "lucide-react";

const UserBookings = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const { getUserInfo } = useKeycloak();

  useEffect(() => {
    fetchUserBookings();
  }, []);

  const fetchUserBookings = async () => {
    try {
      setLoading(true);
      const userInfo = getUserInfo();
      const userId = userInfo?.sub || userInfo?.id;

      if (!userId) {
        throw new Error("User ID not found");
      }

      // Fetch only user's own bookings
      const response = await bookingService.getUserBookings(userId, {
        limit: 50, // Show more bookings for users
      });

      setBookings(response.data || response || []);
    } catch (err) {
      console.error("Error fetching user bookings:", err);
      setError("Failed to load your bookings. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const handleBookingClick = (booking) => {
    setSelectedBooking(selectedBooking?.id === booking.id ? null : booking);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-blue-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading your bookings...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-8 h-8 text-red-500 mx-auto mb-4" />
          <p className="text-red-600">{error}</p>
          <button
            onClick={fetchUserBookings}
            className="mt-4 px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">My Bookings</h1>
        <p className="text-gray-600">View and manage all your event bookings</p>
      </div>

      {bookings.length === 0 ? (
        <div className="text-center py-12">
          <div className="bg-gray-50 rounded-lg p-8">
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No bookings found
            </h3>
            <p className="text-gray-600 mb-4">
              You haven&apos;t made any bookings yet.
            </p>
            <button className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 hidden">
              Create Your First Booking
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {bookings.map((booking) => (
            <Bookings
              key={booking.id}
              booking={booking}
              isSelected={selectedBooking?.id === booking.id}
              onClick={handleBookingClick}
            />
          ))}
        </div>
      )}

      {selectedBooking && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-4">
              <h2 className="text-xl font-semibold">Booking Details</h2>
              <button
                onClick={() => setSelectedBooking(null)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <strong>Title:</strong> {selectedBooking.title || "N/A"}
              </div>
              <div>
                <strong>Status:</strong> {selectedBooking.status || "N/A"}
              </div>
              <div>
                <strong>Date:</strong>{" "}
                {selectedBooking.timeSlot?.startTime
                  ? new Date(
                      selectedBooking.timeSlot.startTime,
                    ).toLocaleDateString()
                  : "No date"}
              </div>
              <div>
                <strong>Total:</strong> €{selectedBooking.total || "N/A"}
              </div>
              {/* Add more booking details as needed */}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserBookings;
